import { useCallback, useEffect, useRef, useState } from 'react';
import {
  addDocumentSigner,
  addOwnerAsParty,
  cancelSignRequests,
  dictateAgreement,
  dictateAgreementText,
  EngineError,
  getCaseDetail,
  getEditableStipulation,
  getSigningStatus,
  removeDocumentSigner,
  saveStipulationBody,
  sendSignRequests,
  updateDocumentSigner,
  type EditableStipulation,
} from '../../api/engineDashboard';
import type { SigningStatus } from '../../types/dashboard';
import { confirmDialog } from '../../lib/confirmDialog';

/**
 * Full-page stipulation editor (Mo's Loom, 2026-09-15) — the old portal's
 * beloved workflow rebuilt on the engine: the WHOLE document visible in
 * print form, the agreement region freely editable as rich text, a
 * Templates dropdown for the court caption, autosave, and Get Signatures
 * with per-signer copyable links. "Trying to do it from this little
 * screen … is what's creating headache and heartache for the mediator."
 */

interface Props {
  token: string;
  agreementId: string;
  caseId: string;
  caseTitle?: string | null;
  onBack: () => void;
  onUnauthorized: () => void;
}

const EDITABLE_STATUSES = ['draft', 'review', 'approved'];

export function StipulationEditorPage({ token, agreementId, caseId, caseTitle, onBack, onUnauthorized }: Props) {
  const [doc, setDoc] = useState<EditableStipulation | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<'saved' | 'saving' | 'dirty'>('saved');
  const [signPanel, setSignPanel] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [fieldEdits, setFieldEdits] = useState<{ case_no: string; plaintiff: string; defendants: string; dated_on: string; division: string; courtroom: string } | null>(null);
  const [genOpen, setGenOpen] = useState(false);
  const [genText, setGenText] = useState('');
  const [genState, setGenState] = useState<'idle' | 'recording' | 'working'>('idle');
  const recRef = useRef<MediaRecorder | null>(null);
  const recChunks = useRef<Blob[]>([]);
  const [newSigner, setNewSigner] = useState<{ name: string; role_label: string; email: string; phone: string } | null>(null);
  const [editingSigner, setEditingSigner] = useState<{ id: string; name: string; role_label: string; email: string; phone: string } | null>(null);
  const [signing, setSigning] = useState<SigningStatus | null>(null);
  const [sendBusy, setSendBusy] = useState(false);
  // AI-review acknowledgment (Danny, 2026-10-31): gates sending links.
  const [aiReviewed, setAiReviewed] = useState(false);
  const [copiedSig, setCopiedSig] = useState<string | null>(null);
  // Property owner on file (owner directory) — powers the one-click
  // "add owner as signer" (Rachel/Danny, 2026-10-30: owner must be addable
  // on the dashboard, not just in Zoom).
  const [ownerOnFile, setOwnerOnFile] = useState<{ name: string; email: string | null; phone: string | null } | null>(null);
  const editorRef = useRef<HTMLDivElement | null>(null);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestHtml = useRef<string>('');

  useEffect(() => {
    // Signers are now surfaced on the page itself (not only inside the
    // sheet), so load signing status + the owner-on-file up front.
    void refreshSigning();
    getCaseDetail(token, caseId)
      .then((detail) => {
        const o = detail.property?.owner;
        if (o?.name) setOwnerOnFile({ name: o.name, email: o.email ?? null, phone: o.phone ?? null });
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId, agreementId]);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await getEditableStipulation(token, agreementId);
      setDoc(res);
      latestHtml.current = res.body_html;
      if (editorRef.current) editorRef.current.innerHTML = res.body_html;
    } catch (err) {
      if (err instanceof EngineError && err.status === 401) return onUnauthorized();
      setError(err instanceof EngineError ? err.message : 'Could not load the stipulation.');
    }
  }, [token, agreementId, onUnauthorized]);

  useEffect(() => {
    void load();
  }, [load]);

  // Seed the contentEditable exactly once per load — React must never
  // re-render its children or the caret jumps.
  const mountEditor = (el: HTMLDivElement | null) => {
    editorRef.current = el;
    if (el && doc && el.dataset.seeded !== agreementId) {
      el.innerHTML = latestHtml.current || doc.body_html;
      el.dataset.seeded = agreementId;
    }
  };

  const editable = doc ? EDITABLE_STATUSES.includes(doc.status) : false;
  const isResolution = doc?.doc_type === 'resolution_agreement' || doc?.doc_type === 'certificate_of_attempts';

  const flushSave = useCallback(async () => {
    if (!editorRef.current) return;
    const html = editorRef.current.innerHTML;
    latestHtml.current = html;
    setSaveState('saving');
    try {
      await saveStipulationBody(token, agreementId, { body_html: html });
      setSaveState('saved');
    } catch (err) {
      if (err instanceof EngineError && err.status === 401) return onUnauthorized();
      setSaveState('dirty');
      setError(err instanceof EngineError ? err.message : 'Autosave failed — your latest edits are not saved yet.');
    }
  }, [token, agreementId, onUnauthorized]);

  const onInput = () => {
    setSaveState('dirty');
    setError(null);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => void flushSave(), 1200);
  };

  useEffect(() => () => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
  }, []);

  const exec = (command: string) => {
    editorRef.current?.focus();
    document.execCommand(command);
    onInput();
  };

  const applyTemplate = async (key: string) => {
    if (!doc) return;
    const tpl = doc.templates.find((t) => t.key === key);
    if (!tpl) return;
    // A template that carries its own body (e.g. the JeffCo eviction form)
    // REPLACES the agreement body with the blank form to fill in — confirm so
    // a mediator doesn't wipe work by re-picking it.
    if (tpl.body_html && !window.confirm(`Load the "${tpl.label}" form? This replaces the current agreement body with the blank form to fill in.`)) {
      return;
    }
    // Save any pending body edits first so nothing is lost on reload.
    if (saveTimer.current) clearTimeout(saveTimer.current);
    await flushSave();
    setSaveState('saving');
    try {
      await saveStipulationBody(token, agreementId, {
        caption_overrides: tpl.caption_overrides,
        ...(tpl.body_html ? { body_html: tpl.body_html } : {}),
      });
      await load();
      setSaveState('saved');
    } catch (err) {
      if (err instanceof EngineError && err.status === 401) return onUnauthorized();
      setSaveState('dirty');
      setError(err instanceof EngineError ? err.message : 'Could not switch the template.');
    }
  };

  const currentTemplateKey = (() => {
    if (!doc) return 'default';
    const cur = JSON.stringify(doc.caption_overrides ?? null);
    const hit = doc.templates.find((t) => JSON.stringify(t.caption_overrides ?? null) === cur);
    return hit?.key ?? 'default';
  })();

  const refreshSigning = async () => {
    try {
      setSigning(await getSigningStatus(token, agreementId));
    } catch (err) {
      if (err instanceof EngineError && err.status === 401) return onUnauthorized();
      setError('Could not load signing status.');
    }
  };

  // After a signer is renamed / added / removed, refresh ONLY the rendered
  // signature block (and status) so the printed document matches — without
  // re-seeding the contentEditable body (that would discard unsaved edits
  // and jump the caret).
  const refreshSignatureBlock = async () => {
    try {
      const res = await getEditableStipulation(token, agreementId);
      setDoc((d) => (d ? { ...d, signature_html: res.signature_html, status: res.status } : res));
    } catch {
      /* non-fatal — the Signatures panel already reflects the change */
    }
  };

  const openSignatures = async () => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    if (saveState !== 'saved') await flushSave();
    setSignPanel(true);
    void refreshSigning();
  };

  const sendLinks = async () => {
    if (!(await confirmDialog('Email/text each pending signer their private signing link?', { danger: true, confirmLabel: 'Send links' }))) return;
    setSendBusy(true);
    setError(null);
    try {
      await sendSignRequests(token, agreementId);
      setDoc((d) => (d ? { ...d, status: 'sent' } : d));
      void refreshSigning();
    } catch (err) {
      if (err instanceof EngineError && err.status === 401) return onUnauthorized();
      setError(err instanceof EngineError ? err.message : 'Could not send signing links.');
    } finally {
      setSendBusy(false);
    }
  };

  const copyLink = async (sigId: string, url: string) => {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      window.prompt('Copy the signing link:', url);
    }
    setCopiedSig(sigId);
    setTimeout(() => setCopiedSig((prev) => (prev === sigId ? null : prev)), 2000);
  };

  // ── Case-details form (header/footer fields) ─────────────────────
  const openDetails = () => {
    if (!doc?.fields) return;
    setFieldEdits({
      case_no: doc.fields.case_no,
      plaintiff: doc.fields.plaintiff,
      defendants: doc.fields.defendants.join('\n'),
      dated_on: doc.fields.dated_on,
      division: doc.fields.division ?? '',
      courtroom: doc.fields.courtroom ?? '',
    });
    setDetailsOpen(true);
  };
  const saveDetails = async () => {
    if (!fieldEdits || !doc) return;
    setSaveState('saving');
    try {
      await saveStipulationBody(token, agreementId, {
        caption_overrides: {
          ...((doc.caption_overrides ?? {}) as Record<string, unknown>),
          case_no: fieldEdits.case_no.trim(),
          plaintiff: fieldEdits.plaintiff.trim(),
          defendants: fieldEdits.defendants.split('\n').map((x) => x.trim()).filter(Boolean),
          dated_on: fieldEdits.dated_on,
          division: fieldEdits.division.trim() || null,
          courtroom: fieldEdits.courtroom.trim() || null,
        },
      });
      setDetailsOpen(false);
      await load();
      setSaveState('saved');
    } catch (err) {
      if (err instanceof EngineError && err.status === 401) return onUnauthorized();
      setSaveState('dirty');
      setError(err instanceof EngineError ? err.message : 'Could not save the case details.');
    }
  };

  // ── Generator (dictate or describe) inside the editor ────────────
  const generateFromText = async () => {
    if (genText.trim().length < 20) { setError('Describe the resolution in a few sentences first.'); return; }
    setGenState('working');
    setError(null);
    try {
      await dictateAgreementText(token, caseId, genText.trim());
      setGenOpen(false);
      setGenText('');
      if (editorRef.current) delete editorRef.current.dataset.seeded;
      await load();
      if (editorRef.current && doc) editorRef.current.dataset.seeded = '';
    } catch (err) {
      if (err instanceof EngineError && err.status === 401) return onUnauthorized();
      setError(err instanceof EngineError ? err.message : 'Generation failed — try again or type the terms directly.');
    } finally {
      setGenState('idle');
    }
  };
  const toggleDictate = async () => {
    if (genState === 'recording') { recRef.current?.stop(); return; }
    setError(null);
    // The Zoom client's built-in browser exposes no microphone API at
    // all (same family as its window.confirm block) — detect that first
    // so the message says what to actually do instead of a generic
    // permission complaint (Danny, 2026-09-16: "not even an option to
    // turn it on").
    if (!navigator.mediaDevices?.getUserMedia) {
      setError('This window cannot record audio — the Zoom app\'s built-in browser blocks the microphone. Open dash.belevelheaded.com in Chrome or Safari to dictate, or use ✍️ Describe terms to type it instead.');
      return;
    }
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      const name = (err as DOMException)?.name ?? '';
      setError(
        name === 'NotFoundError'
          ? 'No microphone was found on this device. Use ✍️ Describe terms to type the resolution instead.'
          : name === 'NotAllowedError'
          ? 'Microphone access is blocked. Click the camera/mic icon in the address bar to allow it (on a Mac, also check System Settings → Privacy → Microphone for your browser), then try again — or use ✍️ Describe terms.'
          : 'Could not start recording (' + (name || 'unknown error') + '). Try ✍️ Describe terms to type the resolution instead.',
      );
      return;
    }
    const rec = new MediaRecorder(stream);
    recChunks.current = [];
    rec.ondataavailable = (e) => { if (e.data.size > 0) recChunks.current.push(e.data); };
    rec.onstop = async () => {
      stream.getTracks().forEach((t) => t.stop());
      const audio = new Blob(recChunks.current, { type: rec.mimeType || 'audio/webm' });
      if (audio.size < 2000) { setGenState('idle'); setError('That recording was empty — try again.'); return; }
      setGenState('working');
      try {
        await dictateAgreement(token, caseId, audio);
        if (editorRef.current) delete editorRef.current.dataset.seeded;
        await load();
      } catch (err) {
        if (err instanceof EngineError && err.status === 401) return onUnauthorized();
        setError(err instanceof EngineError ? err.message : 'Dictation drafting failed — try again or type the terms.');
      } finally {
        setGenState('idle');
      }
    };
    rec.start();
    recRef.current = rec;
    setGenState('recording');
  };

  // ── Signer management ────────────────────────────────────────────
  const saveSignerEdit = async () => {
    if (!editingSigner) return;
    try {
      await updateDocumentSigner(token, agreementId, editingSigner.id, {
        name: editingSigner.name,
        role_label: editingSigner.role_label,
        email: editingSigner.email.trim() || undefined,
        phone: editingSigner.phone.trim() || undefined,
      });
      setEditingSigner(null);
      void refreshSigning();
      void refreshSignatureBlock();
    } catch (err) {
      if (err instanceof EngineError && err.status === 401) return onUnauthorized();
      setError(err instanceof EngineError ? err.message : 'Could not update the signer.');
    }
  };
  const addSigner = async () => {
    if (!newSigner?.name.trim()) return;
    try {
      await addDocumentSigner(token, agreementId, {
        name: newSigner.name.trim(),
        role_label: newSigner.role_label.trim() || undefined,
        email: newSigner.email.trim() || undefined,
        phone: newSigner.phone.trim() || undefined,
      });
      setNewSigner(null);
      void refreshSigning();
      void refreshSignatureBlock();
    } catch (err) {
      if (err instanceof EngineError && err.status === 401) return onUnauthorized();
      setError(err instanceof EngineError ? err.message : 'Could not add the signer.');
    }
  };
  const deleteSigner = async (sigId: string, name: string) => {
    if (!(await confirmDialog(`Remove ${name} as a signer? Their signing link stops working.`, { danger: true, confirmLabel: 'Remove' }))) return;
    try {
      await removeDocumentSigner(token, agreementId, sigId);
      void refreshSigning();
      void refreshSignatureBlock();
    } catch (err) {
      if (err instanceof EngineError && err.status === 401) return onUnauthorized();
      setError(err instanceof EngineError ? err.message : 'Could not remove the signer.');
    }
  };

  const reopenForEdits = async () => {
    if (!doc) return;
    const fullySigned = doc.status === 'signed';
    const msg = fullySigned
      ? 'This document is FULLY SIGNED. Void ALL collected signatures and reopen it for edits? Every party will need to re-sign the edited version.'
      : 'Cancel all outstanding signing links and pull this document back for edits? Anyone who already signed will need to re-sign the edited version.';
    if (!(await confirmDialog(msg, { danger: true, confirmLabel: fullySigned ? 'Void & reopen' : 'Cancel signing' }))) return;
    try {
      const res = await cancelSignRequests(token, agreementId, { reopenSigned: fullySigned });
      setDoc((d) => (d ? { ...d, status: 'draft' } : d));
      setSigning(null);
      if (res.signatures_reset > 0) {
        window.alert(`${res.signatures_reset} signature(s) reset — edit the document, then send fresh signing links.`);
      }
    } catch (err) {
      if (err instanceof EngineError && err.status === 401) return onUnauthorized();
      setError(err instanceof EngineError ? err.message : 'Could not reopen the document.');
    }
  };

  return (
    <div className="mx-auto max-w-4xl">
      {/* header */}
      <div className="sticky top-0 z-10 -mx-4 mb-4 border-b border-surface-border bg-white/95 px-4 py-2 backdrop-blur">
        <div className="flex flex-wrap items-center gap-3">
          <button type="button" className="text-sm text-brand hover:underline" onClick={onBack}>
            ← Back to case
          </button>
          <h1 className="text-base font-semibold">
            {doc?.doc_type === 'certificate_of_attempts' ? 'Certificate of Attempts' : isResolution ? 'Resolution Agreement' : 'Stipulation'}
            {caseTitle ? <span className="font-normal text-gray-500"> — {caseTitle}</span> : null}
          </h1>
          <span className="text-xs text-gray-400">
            {saveState === 'saved' ? 'Saved' : saveState === 'saving' ? 'Saving…' : 'Unsaved edits'}
          </span>
          <div className="ml-auto flex items-center gap-2">
            {doc && !editable && doc.doc_type !== 'certificate_of_attempts' && (
              <button type="button" className="rounded-md border border-amber-300 px-3 py-1.5 text-sm text-amber-800 hover:bg-amber-50" onClick={() => void reopenForEdits()}>
                {doc.status === 'signed' ? 'Void signatures & edit…' : 'Cancel signing & edit…'}
              </button>
            )}
            {/* Nobody signs a COA — it is Levelheaded's own attestation and
                the court sees it as soon as it exists (Mo, 2026-09-21: the
                Get Signatures button read as a required step). */}
            {doc?.doc_type === 'certificate_of_attempts' ? (
              <button
                type="button"
                className="btn-primary !py-1.5 !text-sm"
                onClick={async () => {
                  await flushSave();
                  onBack();
                }}
              >
                Done — visible to the court
              </button>
            ) : (
              // Once out for signatures, this panel is how you fix a
              // signer without recalling the whole agreement (Danny,
              // 2026-09-29: a party gave their full name only at signing
              // time). Relabel so it doesn't read as "start over".
              <button type="button" className="btn-primary !py-1.5 !text-sm" onClick={() => void openSignatures()}>
                {doc?.status === 'sent' ? 'Manage signers & links' : 'Signers & Get Signatures…'}
              </button>
            )}
          </div>
        </div>
        {doc && (
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {!isResolution && <label className="text-xs font-medium text-gray-600">
              Template{' '}
              <select
                className="ml-1 rounded border border-surface-border px-2 py-1 text-xs"
                value={currentTemplateKey}
                disabled={!editable}
                onChange={(e) => void applyTemplate(e.target.value)}
              >
                {doc.templates.map((t) => (
                  <option key={t.key} value={t.key}>{t.label}</option>
                ))}
              </select>
            </label>}
            {editable && (
              <>
                {!isResolution && (
                  <button
                    type="button"
                    className="rounded-md border border-surface-border px-2 py-1 text-xs text-brand hover:bg-surface-muted"
                    onClick={openDetails}
                  >
                    Edit case details…
                  </button>
                )}
                {!isResolution && (
                  <button
                    type="button"
                    className={`rounded-md border px-2 py-1 text-xs ${genState === 'recording' ? 'border-red-300 bg-red-50 text-red-700' : 'border-surface-border text-brand hover:bg-surface-muted'}`}
                    disabled={genState === 'working'}
                    onClick={() => void toggleDictate()}
                  >
                    {genState === 'recording' ? '⏺ Recording — click to finish' : genState === 'working' ? 'Drafting…' : '🎤 Dictate terms'}
                  </button>
                )}
                {!isResolution && (
                  <button
                    type="button"
                    className="rounded-md border border-surface-border px-2 py-1 text-xs text-brand hover:bg-surface-muted"
                    disabled={genState !== 'idle'}
                    onClick={() => setGenOpen((v) => !v)}
                  >
                    ✍️ Describe terms…
                  </button>
                )}
              </>
            )}
            {editable && (
              <div className="flex items-center gap-1 rounded-md border border-surface-border bg-surface-muted px-1 py-0.5">
                {(
                  [
                    ['undo', '↩', 'Undo'],
                    ['redo', '↪', 'Redo'],
                    ['bold', 'B', 'Bold'],
                    ['italic', 'I', 'Italic'],
                    ['underline', 'U', 'Underline'],
                    ['insertUnorderedList', '••', 'Bulleted list'],
                    ['insertOrderedList', '1.', 'Numbered list'],
                  ] as const
                ).map(([cmd, label, title]) => (
                  <button
                    key={cmd}
                    type="button"
                    title={title}
                    className={`rounded px-2 py-0.5 text-sm hover:bg-white ${cmd === 'bold' ? 'font-bold' : cmd === 'italic' ? 'italic' : cmd === 'underline' ? 'underline' : ''}`}
                    onMouseDown={(e) => {
                      e.preventDefault(); // keep the selection in the editor
                      exec(cmd);
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
            {!editable && doc && (
              <span className="text-xs text-amber-700">
                {doc.status === 'signed' ? 'Fully signed — read-only.' : 'Sent for signatures — read-only until recalled.'}
              </span>
            )}
          </div>
        )}
      </div>

      {detailsOpen && fieldEdits && (
        <div className="mb-3 rounded-lg border border-surface-border bg-white p-4">
          <div className="text-sm font-semibold">Case details on the document</div>
          <div className="mt-2 grid gap-3 sm:grid-cols-2">
            <label className="text-xs text-gray-600">Case number
              <input className="mt-1 w-full rounded border border-surface-border px-2 py-1.5 text-sm" value={fieldEdits.case_no}
                onChange={(e) => setFieldEdits((f) => f && { ...f, case_no: e.target.value })} />
            </label>
            <label className="text-xs text-gray-600">Dated (printed date)
              <input type="date" className="mt-1 w-full rounded border border-surface-border px-2 py-1.5 text-sm" value={fieldEdits.dated_on}
                onChange={(e) => setFieldEdits((f) => f && { ...f, dated_on: e.target.value })} />
            </label>
            <label className="text-xs text-gray-600">Plaintiff(s)
              <input className="mt-1 w-full rounded border border-surface-border px-2 py-1.5 text-sm" value={fieldEdits.plaintiff}
                onChange={(e) => setFieldEdits((f) => f && { ...f, plaintiff: e.target.value })} />
            </label>
            <label className="text-xs text-gray-600">Defendant(s) — one per line
              <textarea rows={2} className="mt-1 w-full rounded border border-surface-border px-2 py-1.5 text-sm" value={fieldEdits.defendants}
                onChange={(e) => setFieldEdits((f) => f && { ...f, defendants: e.target.value })} />
            </label>
            <label className="text-xs text-gray-600">Division
              <input className="mt-1 w-full rounded border border-surface-border px-2 py-1.5 text-sm" value={fieldEdits.division}
                onChange={(e) => setFieldEdits((f) => f && { ...f, division: e.target.value })} />
            </label>
            <label className="text-xs text-gray-600">Courtroom
              <input className="mt-1 w-full rounded border border-surface-border px-2 py-1.5 text-sm" value={fieldEdits.courtroom}
                onChange={(e) => setFieldEdits((f) => f && { ...f, courtroom: e.target.value })} />
            </label>
          </div>
          <div className="mt-3 flex gap-2">
            <button type="button" className="btn-primary !py-1 !text-xs" onClick={() => void saveDetails()}>Save details</button>
            <button type="button" className="text-xs text-gray-500 hover:text-gray-800" onClick={() => setDetailsOpen(false)}>Cancel</button>
          </div>
        </div>
      )}

      {genOpen && (
        <div className="mb-3 rounded-lg border border-surface-border bg-white p-4">
          <div className="text-sm font-semibold">Describe the resolution</div>
          <p className="mt-1 text-xs text-gray-500">
            Write it like you&rsquo;d summarize it to the parties — names, amounts, dates, payment method, what happens
            on default. The generator turns it into formal terms; the current agreement region is replaced.
          </p>
          <textarea
            rows={5}
            className="mt-2 w-full rounded border border-surface-border px-2 py-1.5 text-sm"
            placeholder="e.g. Defendant pays Plaintiff $2,400 in two installments of $1,200 due Oct 1 and Nov 1 by Zelle. If a payment is missed…"
            value={genText}
            onChange={(e) => setGenText(e.target.value)}
          />
          <div className="mt-2 flex gap-2">
            <button type="button" className="btn-primary !py-1 !text-xs" disabled={genState !== 'idle'} onClick={() => void generateFromText()}>
              {genState === 'working' ? 'Drafting…' : 'Generate terms'}
            </button>
            <button type="button" className="text-xs text-gray-500 hover:text-gray-800" onClick={() => setGenOpen(false)}>Cancel</button>
          </div>
        </div>
      )}

      {error && <div className="mb-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}

      {/* the document, print-form */}
      {doc ? (
        <div className="rounded-lg border border-surface-border bg-white p-8 shadow-sm">
          <style>{doc.styles_css}</style>
          <div className="keel-doc">
            {doc.prefix_html && <div dangerouslySetInnerHTML={{ __html: doc.prefix_html }} />}
            <div
              ref={mountEditor}
              contentEditable={editable}
              suppressContentEditableWarning
              spellCheck
              className={`min-h-[120px] rounded-sm px-1 outline-none ${editable ? 'ring-1 ring-blue-200 focus:ring-2 focus:ring-blue-400' : ''}`}
              onInput={onInput}
              onBlur={() => {
                if (saveTimer.current) clearTimeout(saveTimer.current);
                if (saveState !== 'saved') void flushSave();
              }}
            />
            {doc.suffix_html && <div dangerouslySetInnerHTML={{ __html: doc.suffix_html }} />}
            {/* The signature block (current signer names/roles) renders from
                the live signatures rows — reloaded after any signer edit so
                the printed document always matches the Signatures panel. */}
            {doc.signature_html && <div dangerouslySetInnerHTML={{ __html: doc.signature_html }} />}
          </div>
        </div>
      ) : !error ? (
        <div className="py-16 text-center text-sm text-gray-500">Loading the document…</div>
      ) : null}
      {doc && editable && (
        <p className="mt-2 text-center text-xs text-gray-400">
          The outlined region is yours to edit — the caption and court language around it render exactly as they will print.
          Edits save automatically.
        </p>
      )}

      {doc && signing && signing.signers.length > 0 && (
        <div className="mx-auto mt-3 flex max-w-3xl flex-wrap items-center gap-2 rounded-md border border-surface-border bg-surface-muted/40 px-3 py-2 text-xs">
          <span className="font-semibold text-gray-700">Signers:</span>
          {signing.signers.map((s2) => (
            <span key={s2.id} className="rounded-full bg-white px-2 py-0.5 text-gray-700 ring-1 ring-surface-border">
              {s2.signer_name}
              <span className={s2.status === 'signed' ? 'ml-1 text-green-700' : s2.status === 'declined' ? 'ml-1 text-red-600' : 'ml-1 text-amber-700'}>
                · {s2.status}
              </span>
            </span>
          ))}
          <button type="button" className="ml-auto font-medium text-brand hover:underline" onClick={() => void openSignatures()}>
            Manage signers…
          </button>
        </div>
      )}

      {/* signatures side sheet */}
      {signPanel && (
        <div className="fixed inset-0 z-40 flex justify-end bg-black/30" onClick={() => setSignPanel(false)}>
          <div className="h-full w-full max-w-md overflow-y-auto bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h2 className="text-base font-semibold">Signatures</h2>
              <button type="button" className="text-gray-400 hover:text-gray-700" onClick={() => setSignPanel(false)}>✕</button>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Share each participant&rsquo;s personal signing link, or send them all by email/text. Status updates as
              they sign. You can rename, add, or remove a signer here even after links are out — no need to
              recall the agreement (a signer who has already signed is locked).
            </p>
            <div className="mt-4 space-y-2">
              {(signing?.signers ?? []).map((s) => (
                <div key={s.id} className="rounded-md border border-surface-border p-2.5 text-sm">
                  {editingSigner?.id === s.id ? (
                    <div className="space-y-1.5">
                      <input
                        className="w-full rounded border border-surface-border px-2 py-1 text-sm"
                        value={editingSigner.name}
                        placeholder="Signer name"
                        onChange={(e) => setEditingSigner((v) => v && { ...v, name: e.target.value })}
                      />
                      <input
                        className="w-full rounded border border-surface-border px-2 py-1 text-sm"
                        value={editingSigner.role_label}
                        placeholder="Role (e.g. Plaintiff, Attorney for Defendant)"
                        onChange={(e) => setEditingSigner((v) => v && { ...v, role_label: e.target.value })}
                      />
                      <input
                        className="w-full rounded border border-surface-border px-2 py-1 text-sm"
                        type="email"
                        value={editingSigner.email}
                        placeholder="Email (for their signing link)"
                        onChange={(e) => setEditingSigner((v) => v && { ...v, email: e.target.value })}
                      />
                      <input
                        className="w-full rounded border border-surface-border px-2 py-1 text-sm"
                        type="tel"
                        value={editingSigner.phone}
                        placeholder="Mobile (optional — for a texted link)"
                        onChange={(e) => setEditingSigner((v) => v && { ...v, phone: e.target.value })}
                      />
                      <div className="flex gap-2">
                        <button type="button" className="btn-primary !py-0.5 !text-xs" onClick={() => void saveSignerEdit()}>Save</button>
                        <button type="button" className="text-xs text-gray-500" onClick={() => setEditingSigner(null)}>Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center justify-between gap-2">
                        <div>
                          <div className="font-medium">{s.signer_name}</div>
                          {s.role_label && <div className="text-xs text-gray-500">{s.role_label}</div>}
                          {(s.signer_email || s.signer_phone) && (
                            <div className="text-[11px] text-gray-400">{[s.signer_email, s.signer_phone].filter(Boolean).join(' · ')}</div>
                          )}
                        </div>
                        <span
                          className={`rounded-full px-2 py-0.5 text-xs ${
                            s.status === 'signed'
                              ? 'bg-green-100 text-green-800'
                              : s.status === 'declined'
                              ? 'bg-red-100 text-red-700'
                              : 'bg-amber-100 text-amber-800'
                          }`}
                        >
                          {s.status}
                        </span>
                      </div>
                      {s.status === 'pending' && !s.party_id && !s.signer_email && !s.signer_phone && (
                        <div className="mt-1.5 rounded border border-amber-300 bg-amber-50 px-2 py-1 text-[11px] text-amber-900">
                          No email or mobile on file — this signer won&rsquo;t receive a link. Add one with
                          &ldquo;Edit…&rdquo; below, then send the links.
                        </div>
                      )}
                      <div className="mt-1.5 flex flex-wrap items-center gap-2">
                        {s.status === 'pending' && s.sign_url && (
                          <button
                            type="button"
                            className="rounded-md border border-surface-border px-2 py-1 text-xs text-brand hover:bg-surface-muted"
                            onClick={() => void copyLink(s.id, s.sign_url!)}
                          >
                            {copiedSig === s.id ? '✓ Copied' : 'Copy signing link'}
                          </button>
                        )}
                        {s.status !== 'signed' && (
                          <>
                            <button
                              type="button"
                              className="text-xs text-gray-500 hover:text-gray-900"
                              onClick={() => setEditingSigner({ id: s.id, name: s.signer_name, role_label: s.role_label ?? '', email: s.signer_email ?? '', phone: s.signer_phone ?? '' })}
                            >
                              Edit…
                            </button>
                            <button
                              type="button"
                              className="text-xs text-red-600 hover:text-red-800"
                              onClick={() => void deleteSigner(s.id, s.signer_name)}
                            >
                              Remove
                            </button>
                          </>
                        )}
                      </div>
                    </>
                  )}
                </div>
              ))}
              {!signing && <div className="text-xs text-gray-400">Loading signers…</div>}
              {ownerOnFile &&
                !(signing?.signers ?? []).some((s2) => s2.signer_name.trim().toLowerCase().split(/[,\s]+/).sort().join(' ') === ownerOnFile.name.trim().toLowerCase().split(/[,\s]+/).sort().join(' ')) && (
                <button
                  type="button"
                  className="w-full rounded-md border border-dashed border-surface-border px-2.5 py-2 text-left text-xs text-brand hover:bg-surface-muted"
                  onClick={async () => {
                    try {
                      await addOwnerAsParty(token, caseId).catch(() => null); // idempotent; party-side toggle
                      await addDocumentSigner(token, agreementId, {
                        name: ownerOnFile.name,
                        role_label: 'Property Owner',
                        email: ownerOnFile.email ?? undefined,
                        phone: ownerOnFile.phone ?? undefined,
                      });
                      void refreshSigning();
                      void refreshSignatureBlock();
                    } catch (err) {
                      setError(err instanceof EngineError ? err.message : 'Could not add the property owner.');
                    }
                  }}
                >
                  + Add property owner on file: <span className="font-medium">{ownerOnFile.name}</span>
                  {ownerOnFile.email || ownerOnFile.phone ? ' (contact info attached)' : ''}
                </button>
              )}
              {newSigner ? (
                <div className="rounded-md border border-dashed border-surface-border p-2.5">
                  <input
                    className="w-full rounded border border-surface-border px-2 py-1 text-sm"
                    placeholder="Signer name"
                    value={newSigner.name}
                    onChange={(e) => setNewSigner((v) => v && { ...v, name: e.target.value })}
                  />
                  <input
                    className="mt-1.5 w-full rounded border border-surface-border px-2 py-1 text-sm"
                    placeholder="Role (e.g. Attorney for Plaintiff)"
                    value={newSigner.role_label}
                    onChange={(e) => setNewSigner((v) => v && { ...v, role_label: e.target.value })}
                  />
                  <input
                    className="mt-1.5 w-full rounded border border-surface-border px-2 py-1 text-sm"
                    type="email"
                    placeholder="Email (for their signing link)"
                    value={newSigner.email}
                    onChange={(e) => setNewSigner((v) => v && { ...v, email: e.target.value })}
                  />
                  <input
                    className="mt-1.5 w-full rounded border border-surface-border px-2 py-1 text-sm"
                    type="tel"
                    placeholder="Mobile (optional — for a texted link)"
                    value={newSigner.phone}
                    onChange={(e) => setNewSigner((v) => v && { ...v, phone: e.target.value })}
                  />
                  <p className="mt-1 text-[11px] text-gray-400">Add an email or mobile so this signer can be sent their link.</p>
                  <div className="mt-1.5 flex gap-2">
                    <button type="button" className="btn-primary !py-0.5 !text-xs" onClick={() => void addSigner()}>Add signer</button>
                    <button type="button" className="text-xs text-gray-500" onClick={() => setNewSigner(null)}>Cancel</button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  className="text-xs text-brand hover:underline"
                  onClick={() => setNewSigner({ name: '', role_label: '', email: '', phone: '' })}
                >
                  + Add signer
                </button>
              )}
            </div>
            <label className="mt-4 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-2 text-[11px] leading-snug text-amber-900">
              <input type="checkbox" className="mt-0.5 shrink-0" checked={aiReviewed} onChange={(e) => setAiReviewed(e.target.checked)} />
              <span>You acknowledge this stipulation was generated with AI and by checking this box you are confirming you have carefully reviewed the entire document with the Parties to confirm it is the Parties intent and that they understand it's terms. Do not select this box if you have not read and reviewed the resolution agreement in its entirety with the Parties.</span>
            </label>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <button type="button" className="btn-primary !py-1.5 !text-sm" disabled={sendBusy || !aiReviewed} onClick={() => void sendLinks()}>
                {sendBusy ? 'Sending…' : doc?.status === 'sent' ? 'Resend signing links…' : 'Email/text signing links…'}
              </button>
              <button type="button" className="text-xs text-gray-500 hover:text-gray-800" onClick={() => void refreshSigning()}>
                Refresh status
              </button>
            </div>
            {signing?.all_signed && (
              <div className="mt-3 rounded-md bg-green-50 px-3 py-2 text-sm text-green-800">
                All parties have signed — the executed document is complete.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
